// hazardous-chemicals/components/shared.tsx
import React, { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';

/* ---------- Slide-over Drawer ---------- */
export const Drawer: React.FC<{
    open: boolean;
    width?: string;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ open, width = '640px', onClose, children }) => {
    const [mounted, setMounted] = useState(open);
    useEffect(() => {
        if (open) setMounted(true);
        else {
            const t = setTimeout(() => setMounted(false), 280);
            return () => clearTimeout(t);
        }
    }, [open]);
    if (!mounted) return null;
    return (
        <>
            <div
                onClick={onClose}
                className="fixed inset-0 z-30 bg-black/40 transition-opacity duration-300"
                style={{ opacity: open ? 1 : 0 }}
            />
            <div
                className="fixed top-0 right-0 h-screen z-40 bg-card shadow-2xl flex flex-col transition-transform duration-300"
                style={{ width, transform: open ? 'translateX(0)' : 'translateX(100%)' }}
            >
                {children}
            </div>
        </>
    );
};

/* ---------- Labelled read-only cell (Overview grid) ---------- */
export const InfoCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="rounded-lg bg-muted/40 p-3.5">
        <div className="text-[11px] text-muted-foreground mb-1">{label}</div>
        <div className="text-sm font-medium">{children ?? '—'}</div>
    </div>
);

/* ---------- Section card used inside Storage / PPE tabs ---------- */
export const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="rounded-lg bg-muted/40 p-4">
        <div className="text-sm font-semibold mb-1">{title}</div>
        <div className="text-sm text-foreground">{children}</div>
    </div>
);

/* ---------- Confirm dialog (used for soft delete) ---------- */
export const ConfirmDialog: React.FC<{
    title: string;
    message: string;
    confirmLabel?: string;
    destructive?: boolean;
    onConfirm: () => void;
    onClose: () => void;
}> = ({ title, message, confirmLabel = 'Confirm', destructive, onConfirm, onClose }) => (
    <div
        className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
    >
        <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-sm">
            <div className="p-5">
                <div className="flex items-start gap-3">
                    {destructive && (
                        <div className="h-9 w-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center flex-shrink-0">
                            <TriangleAlert size={18} />
                        </div>
                    )}
                    <div>
                        <h3 className="text-base font-semibold">{title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{message}</p>
                    </div>
                </div>
            </div>
            <div className="px-5 py-3.5 border-t border-border flex justify-end gap-2">
                <button onClick={onClose} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    className={`h-10 px-5 rounded-lg text-sm font-medium ${
                        destructive
                            ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                            : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                >
                    {confirmLabel}
                </button>
            </div>
        </div>
    </div>
);
